/**
 * CPG AI Lab — Brand Profile
 *
 * A single canonical brand-strategy record every AI Lab toolkit reads from.
 * Persists to `users.directory` via `PUT /api/auth/profile/directory` — the
 * same store the Members Directory already uses, so a founder authors their
 * brand ONCE and every generator (Packaging, Ads, …) sees it.
 *
 * Fields on this page (in canonical order for the Prompt Builder):
 *   • Brand name              (existing)
 *   • Category                (existing enum)
 *   • Positioning statement   (NEW — 600 char cap)
 *   • Target consumer / ICP   (NEW — 600 char cap)
 *
 * Non-goals: this is intentionally a lean 4-field surface. The full public
 * community profile (city / stage / socials / bio) still lives on Account →
 * Community, and this page reads/writes the same underlying document, so
 * anything typed here shows up in the community card too.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import PageHero from "../../components/PageHero";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Sparkles, Layers } from "lucide-react";

const CATEGORIES = [
  { value: "",           label: "Select a category" },
  { value: "food",       label: "Food" },
  { value: "beverage",   label: "Beverage" },
  { value: "supplement", label: "Supplement" },
  { value: "wellness",   label: "Wellness" },
  { value: "beauty",     label: "Beauty & Personal Care" },
  { value: "pet",        label: "Pet" },
  { value: "other",      label: "Other" },
];

const POSITIONING_HINT =
  "Example: The first single-serve adaptogen latte designed for high-performing " +
  "women who won't touch traditional coffee — clean caffeine, calm focus, no crash.";

const CONSUMER_HINT =
  "Example: 28–45 yr old urban women earning $85k+, health-conscious but time-poor, " +
  "already buying oat milk and functional wellness in Whole Foods or Erewhon.";

export default function BrandProfile() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    brand: "", category: "", positioning: "", target_consumer: "",
  });

  useEffect(() => {
    // Dedicated Brand-Profile endpoint returns just the 4 Lab fields so we
    // hydrate quickly and never accidentally show stale directory data.
    api.get("/brand-profile")
      .then((r) => {
        setForm({
          brand: r.data?.brand || "",
          category: r.data?.category || "",
          positioning: r.data?.positioning || "",
          target_consumer: r.data?.target_consumer || "",
        });
      })
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      // PATCH is field-scoped — other directory fields (bio, city, socials)
      // survive untouched.
      await api.patch("/brand-profile", form);
      toast.success("Brand profile saved — every AI Lab prompt now uses this");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSaving(false); }
  };

  return (
    <AppShell>
      <div className="py-4 md:py-8 w-full" data-testid="brand-profile-page">
        <Link to="/ai-lab" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-shell-side">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to AI Lab
        </Link>
        <PageHero
          eyebrow="CPG AI Lab · Foundation"
          title="Brand Profile"
          subtitle="Author your brand once. Every AI Lab toolkit reads from here — packaging, ads, copy — so nothing has to be re-explained per generation."
        />

        {loading ? (
          <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="mt-6 grid gap-5" data-testid="brand-profile-form">
            <Field label="Brand name" hint="What your product goes to market as." required>
              <input
                data-testid="brand-input-name"
                className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm"
                value={form.brand} onChange={set("brand")}
                placeholder="e.g. Amber & Oak"
                maxLength={80}
              />
            </Field>

            <Field label="Category" hint="Anchors the copy voice, claim language, and buyer set." required>
              <select
                data-testid="brand-input-category"
                className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm"
                value={form.category} onChange={set("category")}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Positioning statement" hint={POSITIONING_HINT}>
              <textarea
                data-testid="brand-input-positioning"
                className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm min-h-[110px]"
                value={form.positioning} onChange={set("positioning")}
                placeholder="Who this is for, what makes it different, and what the promise is."
                maxLength={600}
              />
              <CharCount value={form.positioning} max={600} />
            </Field>

            <Field label="Target consumer" hint={CONSUMER_HINT}>
              <textarea
                data-testid="brand-input-target-consumer"
                className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm min-h-[110px]"
                value={form.target_consumer} onChange={set("target_consumer")}
                placeholder="Demographics, psychographics, where they already shop, brands they love."
                maxLength={600}
              />
              <CharCount value={form.target_consumer} max={600} />
            </Field>

            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                onClick={save}
                disabled={saving || !form.brand || !form.category}
                data-testid="brand-profile-save"
                className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save brand profile"}
              </button>
              <button
                onClick={() => nav("/ai-lab/packaging")}
                data-testid="brand-profile-jump-packaging"
                className="text-sm inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 hover:bg-muted/40"
              >
                <Layers className="w-4 h-4" /> Continue to Packaging AI →
              </button>
            </div>

            <div className="mt-6 rounded-2xl bg-shell-side/5 border border-shell-side/20 p-5">
              <p className="text-[10px] uppercase tracking-widest text-shell-side font-bold flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> How the Lab uses this
              </p>
              <ul className="mt-2 text-sm text-muted-foreground space-y-1.5">
                <li>• Packaging AI injects category + positioning into every hero-mockup prompt</li>
                <li>• Ads / copy toolkits use target consumer for tone-of-voice matching</li>
                <li>• Warren AI in chat reads this so answers stay brand-accurate</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold">
        {label}{required && <span className="ml-1 text-primary">*</span>}
      </span>
      {hint && <span className="block text-xs text-muted-foreground mt-0.5">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function CharCount({ value, max }) {
  const n = (value || "").length;
  const near = n > max * 0.85;
  return (
    <p className={"text-[10px] text-right mt-1 " + (near ? "text-primary" : "text-muted-foreground")}>
      {n} / {max}
    </p>
  );
}
